(() => {
  const SELECTOR = '[data-sc-machinery-carousel]';
  const ROOT_SELECTOR = '[data-sc-machinery-collection]';

  const initializeCarousel = (carousel) => {
    if (!carousel || carousel.dataset.carouselReady === 'true') return;

    const track = carousel.querySelector('[data-sc-machinery-track]');
    const previous = carousel.querySelector('[data-sc-machinery-previous]');
    const next = carousel.querySelector('[data-sc-machinery-next]');
    if (!track || !previous || !next) return;

    carousel.dataset.carouselReady = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragMoved = false;
    let activePointerId = null;

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const syncControls = () => {
      const maxScroll = getMaxScroll();
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= maxScroll - 2;
      previous.disabled = atStart;
      next.disabled = atEnd;
      previous.setAttribute('aria-disabled', String(atStart));
      next.setAttribute('aria-disabled', String(atEnd));
    };

    const requestSync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncControls);
    };

    const getStep = () => {
      const card = track.querySelector('.sc-machinery-collection__product');
      if (!card) return track.clientWidth;
      const styles = getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const cardWidth = card.getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.round((track.clientWidth + gap) / cardWidth));
      return cardWidth * visibleCards;
    };

    const move = (direction) => {
      track.scrollBy({
        left: direction * getStep(),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', requestSync, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      dragMoved = false;
    });

    track.addEventListener('pointermove', (event) => {
      if (activePointerId !== event.pointerId) return;
      const distance = event.clientX - dragStartX;
      if (Math.abs(distance) > 4 && !dragMoved) {
        dragMoved = true;
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      }
      if (!dragMoved) return;
      event.preventDefault();
      track.scrollLeft = dragStartScroll - distance;
    });

    const endDrag = (event) => {
      if (activePointerId !== event.pointerId) return;
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      activePointerId = null;
      track.classList.remove('is-dragging');
      requestSync();
      if (dragMoved) {
        window.setTimeout(() => {
          dragMoved = false;
        }, 0);
      }
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', (event) => {
      if (!dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      dragMoved = false;
    }, true);

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(requestSync);
      observer.observe(track);
    } else {
      window.addEventListener('resize', requestSync, { passive: true });
    }

    requestSync();
  };

  const initialize = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initializeCarousel);
    root.querySelectorAll(ROOT_SELECTOR).forEach((collection) => {
      if (collection.dataset.searchReady === 'true') return;

      const input = collection.querySelector('[data-sc-machinery-search]');
      if (!input) return;

      const cards = Array.from(collection.querySelectorAll('[data-sc-machinery-card]'));
      const clear = collection.querySelector('[data-sc-machinery-search-clear]');
      const empty = collection.querySelector('[data-sc-machinery-search-empty]');
      const count = collection.querySelector('[data-sc-machinery-result-count]');
      const label = collection.querySelector('[data-sc-machinery-result-label]');
      const carousels = Array.from(collection.querySelectorAll('[data-sc-machinery-carousel]'));
      collection.dataset.searchReady = 'true';

      const normalize = (value) => value
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

      const update = () => {
        const query = normalize(input.value);
        let visible = 0;

        cards.forEach((card) => {
          const matches = query === '' || normalize(card.dataset.search || '').includes(query);
          card.hidden = !matches;
          if (matches) visible += 1;
        });

        carousels.forEach((carousel) => {
          const hasVisibleCards = Array.from(carousel.querySelectorAll('[data-sc-machinery-card]')).some((card) => !card.hidden);
          carousel.hidden = !hasVisibleCards;
          const track = carousel.querySelector('[data-sc-machinery-track]');
          if (track && hasVisibleCards) {
            track.scrollLeft = 0;
            track.dispatchEvent(new Event('scroll'));
          }
        });

        if (clear) clear.hidden = query === '';
        if (empty) empty.hidden = visible !== 0;
        if (count) count.textContent = String(visible);
        if (label) label.textContent = visible === 1 ? 'producto disponible' : 'productos disponibles';
      };

      input.addEventListener('input', update);
      clear?.addEventListener('click', () => {
        input.value = '';
        update();
        input.focus();
      });
      update();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
